import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import jwtInterceoptor from "../../components/shared/jwtInterceptor";
import moment from "moment";
import OrgContext from "../../organisation/OrgContext";
import { useNavigate } from "react-router-dom";
import { LinearProgress } from "@mui/material";
import { toast, ToastContainer } from "react-toastify";
import useAuthHelper from "../../helpers/AuthHelper";
import Swal from "sweetalert2";
import smalLogo from "../../assets/images/ElmanagerLogo.png";
import { baseURL } from "../../services/api";

import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import { PDFDownloadLink } from "@react-pdf/renderer";

const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontSize: 10,
  },
  header: {
    marginBottom: 20,
    textAlign: "center",
    fontSize: 14,
    fontWeight: "bold",
  },
  table: {
    display: "table",
    width: "auto",
    borderStyle: "solid",
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
  },
  tableRow: {
    flexDirection: "row",
  },
  tableCol: {
    width: "14.28%",
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 5,
  },
  tableCell: {
    margin: "auto",
    fontSize: 9,
  },
  tableColDate: {
    width: "10%", // Adjusted width for the DATE column
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 5,
  },
  tableColPC: {
    width: "10%", // Adjusted width for the PC N° column
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 5,
  },
  tableColLibelle: {
    width: "30%", // Adjusted width for the LIBELLE column
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 5,
  },
  tableColDebitCredit: {
    width: "12.5%", // Adjusted width for DEBIT and CREDIT columns
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 5,
  },
  tableColDebitCreditHe: {
    width: "25%", // Adjusted width for DEBIT and CREDIT columns
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 5,
  },
  headerPage: {
    flexDirection: "row",
    padding: 5,
    borderWidth: 1.5,
    marginBottom: 5,
  },
  leftSide: {
    flex: 1,
    justifyContent: "space-between",
  },
  centerContainer: {
    flex: 1,
    justifyContent: "space-between",
    alignItems: "center",
  },
  rightSide: {
    flex: 1,
    justifyContent: "flex-end",
    paddingLeft: 40,
  },
});

const RlvBqListe = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthHelper();
  const { organisation } = useContext(OrgContext);
  const [loading, setLoading] = useState(false);
  const [rlvsBq, setRlvsBq] = useState([]);
  const [selectedRlvBq, setSelectedRlvBq] = useState(null);
  const [rlvData, setRlvData] = useState([]);
  const [purchaseAccounts, setPurchaseAccounts] = useState([]);
  const [transactions, setTransaction] = useState([]);
  const [soldeBQ, setSoldeBQ] = useState();
  const [checkedOpBq, setCheckedOpBq] = useState([]);
  const [checkedOps, setCheckedOps] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [readOnly, setReadOnly] = useState(false);
  const [modalLoading, setModalLoading] = useState(false); // Add state for modal loading
  const [actionLoading, setActionLoading] = useState(false); // Add state for action loading
  const [step, setStep] = useState(1);
  useEffect(() => {
    console.log("checkedOps:", checkedOps);
  }, [checkedOps]);
  useEffect(() => {
    console.log("checkedOpBq:", checkedOpBq);
  }, [checkedOpBq]);
  useEffect(() => {
    fetchRlvsBq();
    getPurchaseAccount();
  }, []);
  const getPurchaseAccount = async () => {
    // We run axios, a promise is returned
    await jwtInterceoptor
      .get(baseURL + `/data/user/accounts/${organisation.id}`)
      .then((response) => {
        setPurchaseAccounts(response.data.data);
      })
      .catch((error) =>
        console.log("error while retrieving purshaseaccounts : " + error)
      );
  };
  const fetchRlvsBq = async () => {
    setLoading(true);
    try {
      const response = await jwtInterceoptor.get(
        `/data/rlvs/${organisation.id}`
      );
      setRlvsBq(response.data.rlvs);
    } catch (error) {
      console.error(error);
      toast.error(t("Erreur lors du chargement des relevés bancaires"));
    } finally {
      setLoading(false);
      console.log(rlvsBq);
    }
  };
  const fetchBqTransaction = async () => {
    if (selectedRlvBq) {
      setModalLoading(true); // Start loading
      try {
        const date = selectedRlvBq.date.split("/");
        const monthRl = `${date[1]}-${date[0]}`;
        const params = {
          month: monthRl,
          payment_account: selectedRlvBq.compte_bancaire,
        };

        const response = await jwtInterceoptor.get(
          `data/bqTransaction/${organisation.id}`,
          { params }
        );

        console.log(response.data);
        setTransaction(response.data.transactions);

        // Initialisation des objets checkedOps et checkedOpBq
        const newCheckedOps =
          rlvData?.map((op, index) => {
            const linkedTransaction = response.data.transactions?.find((tr) => {
              return tr.contrepartie?.rlvOp === op.id;
            });

            return {
              ...op,
              indexOp: index,
              indexBq: op.comptaliser
                ? response.data.transactions.indexOf(linkedTransaction) // ✅ linkedTransaction est un objet et non un booléen
                : null,
              checked: op.comptaliser || !!linkedTransaction,
            };
          }) || [];

        const newCheckedOpBq =
          response.data.transactions?.map((tr, index) => {
            const linkedOperation = rlvData?.find(
              (op) => op.id === tr.contrepartie?.rlvOp
            );

            return {
              ...tr,
              indexOp: linkedOperation ? tr.indexOp : null,
              indexBq: index,
              checked: tr.comptaliser || !!linkedOperation,
            };
          }) || [];

        setCheckedOps(newCheckedOps);
        setCheckedOpBq(newCheckedOpBq);
        setRlvData(newCheckedOps);
        setTransaction(newCheckedOpBq);

        setSoldeBQ(response.data.solde);
      } catch (e) {
        console.error(e);
        toast.error(t("Erreur lors du chargement des relevés bancaires"));
      } finally {
        setModalLoading(false); // Stop loading
      }
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR"); // Ex: 01/01/2024
  };

  useEffect(() => {
    if (selectedRlvBq) {
      fetchBqTransaction();
    }
  }, [readOnly, selectedRlvBq]);

  const handleRowSelection = (row, isReadOnlyMode) => {
    setStep(1);
    setReadOnly(isReadOnlyMode); // Set the mode (read-only or editable)
    setSelectedRlvBq(row); // Set the selected row
    console.log("row", row);
    const filteredRlvData = rlvsBq.filter((r) => r.id === row.id);

    if (filteredRlvData.length !== 0) {
      window.$("#modalRlvBq").modal("show");
    }

    // Initialize checkedOps based on operation_rlvs
    const newCheckedOps = filteredRlvData[0]?.operation_rlvs?.map(
      (op, index) => ({
        ...op,
        indexOp: index,
        indexBq: null,
        checked: false,
        solde: filteredRlvData[0].solde,
      })
    );
    setRlvData(newCheckedOps);
    setCheckedOps(newCheckedOps);
    setSelectedOption("all");
    fetchBqTransaction();
  };

  const handleDeleteRlv = async (rlvId) => {
    Swal.fire({
      title: t("Êtes-vous sûr ?"),
      text: t("Cette action est irréversible !"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: t("Oui, supprimer !"),
      cancelButtonText: t("Annuler"),
    }).then(async (result) => {
      if (result.isConfirmed) {
        setActionLoading(true); // Start loading
        try {
          await jwtInterceoptor.delete(`/data/rlv/${organisation.id}/${rlvId}`);
          toast.success(t("Relevé bancaire a été supprimé avec succès"));
          fetchRlvsBq(); // Refresh the list after deletion
        } catch (error) {
          console.error(error);
          toast.error(t("Erreur lors de la suppression du relevé bancaire"));
        } finally {
          setActionLoading(false); // Stop loading
        }
      }
    });
  };

  const handleDownloadDraft = async (rlvId) => {
    try {
      const response = await jwtInterceoptor.get(
        `/data/draft/rlvs/download/${rlvId}`,
        {
          responseType: "blob", // Ensure the response is treated as a file
        }
      );

      const contentType = response.headers["content-type"]; // Get the file's MIME type
      const fileExtension = contentType.split("/")[1]; // Extract the file extension (e.g., pdf, jpeg)

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `draft_rlv_${rlvId}.${fileExtension}`); // Set the file name with the correct extension
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success(t("Brouillon a été télécharger avec succès"));
    } catch (error) {
      console.error(error);
      toast.error(t("Erreur lors du téléchargement du brouillon"));
    }
  };

  const columns = [
    {
      field: "id",
      headerName: "Relevés ID",
      width: 50,
      flex: 1,
      valueGetter: (params) => `RLV-${params.row.id}`,
    },
    { field: "date", headerName: t("Date"), width: 120, flex: 1 },
    {
      field: "solde",
      headerName: t("Solde"),
      width: 120,
      flex: 1,
      valueGetter: (params) => params.row.solde.toFixed(2, ".", " "),
    },
    {
      field: "account",
      headerName: t("Account"),
      width: 180,
      flex: 1,
      valueGetter: (params) => params.row.account?.code || "-",
    },
    {
      width: 180,
      field: "Date d'importation",
      headerName: t("Date d'importation"),
      flex: 1,
      valueGetter: (params) =>
        `${moment(params.row.created_at).format("DD/MM/YYYY") || ""}`,
    },
    {
      field: "actions",
      headerName: t("Actions"),
      width: 200,
      flex: 1,
      renderCell: (params) => (
        <div className="btn-list">
          <button
            id="bDetail"
            type="button"
            className="btn btn-sm btn-primary"
            onClick={() => handleRowSelection(params.row, true)} // Detail mode (read-only)
          >
            <span className="fe fe-eye"> </span>
          </button>
          <button
            id="bDel"
            type="button"
            className="btn btn-sm btn-danger"
            onClick={() => handleDeleteRlv(params.row.id)} // Delete RLV
          >
            <span className="fe fe-trash-2"> </span>
          </button>
          <button
            id="bPrint"
            type="button"
            className="btn btn-sm btn-warning"
            onClick={() => handleDownloadDraft(params.row.id)} // Download draft
          >
            <span className="fe fe-save"> </span>
          </button>
          <button
            id="bEdit"
            type="button"
            style={{ backgroundColor: "#f1461d", color: "white" }}
            className="btn btn-sm"
            onClick={() => handleRowSelection(params.row, false)} // Edit mode (editable)
          >
            <span className="fe fe-edit"> </span>
          </button>
        </div>
      ),
    },
  ];

  const handleCheckboxChangeOp = (event, index) => {
    const isChecked = event.target.checked;
    setCheckedOps((prevCheckedOps = []) => {
      console.log("Previous state:", prevCheckedOps);
      if (!Array.isArray(prevCheckedOps)) {
        console.error("prevCheckedOps is not an array:", prevCheckedOps);
        return [];
      }
      const exists = prevCheckedOps.find((e) => e.indexOp === index);
      const otherExist = prevCheckedOps.find(
        (e) => e.indexOp !== index && e.indexBq === null && e.checked === true
      );
      console.log(exists);
      console.log(otherExist);
      if (!otherExist) {
        if (exists) {
          return prevCheckedOps.map((item) =>
            item.indexOp === index ? { ...item, checked: isChecked } : item
          );
        }
      } else {
        toast.error(t("Choisissez une opération bancaire, s'il vous plaît."), {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: true,
          progress: undefined,
          theme: "colored",
        });
        return prevCheckedOps;
      }
      return prevCheckedOps;
    });
  };

  const handleCheckboxChangeBq = (event, index) => {
    const isChecked = event.target.checked;

    setCheckedOpBq((prevCheckedOpB = []) => {
      console.log("Previous state:", prevCheckedOpB);
      if (!Array.isArray(prevCheckedOpB)) {
        console.error("prevCheckedOpB is not an array:", prevCheckedOpB);
        return [];
      }

      const exists = prevCheckedOpB.find((e) => e.indexBq === index);
      const existsContre = checkedOps.find(
        (e) => (e.indexBq === null && e.checked === true) || e.indexBq === index
      );

      console.log("existsContre:", existsContre);
      console.log("exists:", exists);

      if (!existsContre) {
        toast.error(t("Merci de choisir premièrement l'opération !"), {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: true,
          progress: undefined,
          theme: "colored",
        });
        return prevCheckedOpB;
      }

      // Vérification si le débit et le crédit de la contrepartie correspondent
      const operationBq = prevCheckedOpB.find((item) => item.indexBq === index);
      const operationOp = checkedOps.find(
        (item) => item.indexOp === existsContre.indexOp
      );

      if (operationBq && operationOp) {
        const totalDebitOp = operationOp.transactions?.reduce(
          (sum, tr) => sum + (tr.debit || 0),
          0
        );
        const totalCreditOp = operationOp.transactions?.reduce(
          (sum, tr) => sum + (tr.credit || 0),
          0
        );

        // if (
        //   totalDebitOp !== operationBq.debit ||
        //   totalCreditOp !== operationBq.credit
        // ) {
        //   toast.error(
        //     t(
        //       "Le débit ou le crédit de la contrepartie ne correspond pas à l'opération bancaire."
        //     ),
        //     {
        //       position: "top-right",
        //       autoClose: 5000,
        //       hideProgressBar: false,
        //       closeOnClick: true,
        //       pauseOnHover: false,
        //       draggable: true,
        //       progress: undefined,
        //       theme: "colored",
        //     }
        //   );
        //   return prevCheckedOpB;
        // }
      }

      setCheckedOps((prev) =>
        prev.map((item) =>
          item.indexOp === existsContre.indexOp
            ? { ...item, indexBq: isChecked ? index : null, checked: isChecked }
            : item
        )
      );

      return exists
        ? prevCheckedOpB.map((item) =>
            item.indexBq === index
              ? { ...item, checked: isChecked, comptaliser: isChecked }
              : item
          )
        : prevCheckedOpB;
    });
  };

  const handleFilterChange = (filterValue) => {
    setSelectedOption(filterValue);

    // Filter and update checkedOpBq
    const filteredCheckedOpBq = transactions
      .filter((transaction) => {
        if (filterValue === "comptabliser")
          return !!transaction.checked === true;
        if (filterValue === "non_comptabliser")
          return !!transaction.checked === false;
        return true; // Show all for "all"
      })
      .map((transaction, index) => {
        const linkedOp = rlvData.find(
          (op) => op.id === transaction.contrepartie?.rlvOp
        );
        console.log("linkedOp", linkedOp);
        return {
          ...transaction,
          indexOp: linkedOp ? linkedOp.indexOp : null,
          indexBq: linkedOp ? linkedOp.indexBq : index,
          checked: transaction.checked,
        };
      });

    setCheckedOpBq(filteredCheckedOpBq);

    // Filter and update checkedOps
    const filteredCheckedOps = rlvData
      .filter((operation) => {
        if (filterValue === "comptabliser") return !!operation.checked === true;
        if (filterValue === "non_comptabliser")
          return !!operation.checked === false;
        return true; // Show all for "all"
      })
      .map((operation, index) => {
        const linkedTransaction = transactions.find(
          (transaction) => transaction.contrepartie?.rlvOp === operation.id
        );
        console.log("linkedTransaction", linkedTransaction);
        return {
          ...operation,
          indexOp: linkedTransaction ? linkedTransaction.indexOp : index,
          indexBq: linkedTransaction ? linkedTransaction.indexBq : null,
          checked: operation.checked,
        };
      });

    setCheckedOps(filteredCheckedOps);
  };

  const calculateTotalsop = (data, field) => {
    console.log("calculateTotalsop - data:", data);
    console.log("calculateTotalsop - field:", field);
    return data.reduce((sum, item) => sum + (item.transactions?.[0]?.[field] || 0), 0);
  };

  const calculateTotalsbq = (data, field) => {
    console.log("calculateTotalsbq - data:", data);
    console.log("calculateTotalsbq - field:", field);
    return data.reduce((sum, item) => sum + (item[field] || 0), 0);
  };

  // Add logs before rendering
  console.log("Rendering checkedOps:", checkedOps);
  console.log("Rendering checkedOpBq:", checkedOpBq);

  // Ensure rendering logic uses the correct state
  const totalDebitOps =
    (rlvData[0]?.solde>0?rlvData[0]?.solde : 0|| 0) +
    calculateTotalsop(
      checkedOps.filter((op) => !op.checked),
      "debit"
    );

  const totalCreditOps =
  Math.abs(rlvData[0]?.solde<0?rlvData[0]?.solde : 0|| 0) +
    calculateTotalsop(
      checkedOps.filter((op) => !op.checked),
      "credit"
    );

  const totalDebitBq =
    (soldeBQ>0?soldeBQ:0 || 0) +
    calculateTotalsbq(
      checkedOpBq.filter((tr) => !tr.checked),
      "debit"
    );

  const totalCreditBq =
  Math.abs(soldeBQ<0?soldeBQ:0 || 0) +
    calculateTotalsbq(
      checkedOpBq.filter((tr) => !tr.checked),
      "credit"
    );

  console.log("Rendering totals:");
  console.log("totalDebitOps:", totalDebitOps);
  console.log("totalCreditOps:", totalCreditOps);
  console.log("totalDebitBq:", totalDebitBq);
  console.log("totalCreditBq:", totalCreditBq);

  const handelSave = async () => {
    setActionLoading(true); // Start loading
    setSelectedOption("all");
    const data = {
      rlv_id: selectedRlvBq.id,
      transactions: checkedOps
        .filter((item) => item.checked === true)
        .map((item) => ({
          operation_id: item.id,
          operation_bq: checkedOpBq.find((op) => op.indexBq === item.indexBq)
            .id,
          typeInvoice: checkedOpBq.find((op) => op.indexBq === item.indexBq)
            .type,
        })),
    };
    console.log("data", data);
    try {
      const response = await jwtInterceoptor.post(
        `/data/rlv/rapproche/${organisation.id}`,
        data
      );
      console.log("response", response);
      toast.success(t("Opérations enregistrées avec succès"));

      // Re-fetch the updated RLV data
      await fetchRlvsBq();
      const updatedRlv = rlvsBq.find((rlv) => rlv.id === selectedRlvBq.id);
      if (updatedRlv) {
        setSelectedRlvBq(updatedRlv);

        // Re-fetch transactions for the updated RLV
        const transactionResponse = await jwtInterceoptor.get(
          `data/bqTransaction/${organisation.id}`,
          {
            params: {
              month: `${updatedRlv.date.split("/")[1]}-${
                updatedRlv.date.split("/")[0]
              }`,
              payment_account: updatedRlv.compte_bancaire,
            },
          }
        );

        const updatedTransactions = transactionResponse.data.transactions;
        setTransaction(updatedTransactions);

        // Reset checkedOps and checkedOpBq based on the updated data
        const updatedCheckedOps =
          updatedRlv.operation_rlvs?.map((op, index) => {
            const linkedTransaction = updatedTransactions.find(
              (tr) => tr.contrepartie?.rlvOp === op.id
            );

            return {
              ...op,
              indexOp: index,
              indexBq: linkedTransaction
                ? updatedTransactions.indexOf(linkedTransaction)
                : null,
              checked: !!linkedTransaction,
            };
          }) || [];
        setCheckedOps(updatedCheckedOps);

        const updatedCheckedOpBq = updatedTransactions.map((tr, index) => {
          const linkedOperation = updatedRlv.operation_rlvs?.find(
            (op) => op.id === tr.contrepartie?.rlvOp
          );

          return {
            ...tr,
            indexOp: linkedOperation
              ? updatedRlv.operation_rlvs.indexOf(linkedOperation)
              : null,
            indexBq: index,
            checked: !!linkedOperation,
          };
        });
        setCheckedOpBq(updatedCheckedOpBq);
      }
    } catch (e) {
      console.error(e);
      toast.error(t("Erreur lors de l'enregistrement des opérations"));
    } finally {
      setActionLoading(false); // Stop loading
    }
  };

  const ExportRlv = () => {
    const lastDay = (() => {
      const [month, year] = (selectedRlvBq?.date || "").split("/");
      return new Date(year, month, 0).toLocaleDateString("fr-FR");
    })();

    const totalDebitOps =
      (rlvData[0]?.solde > 0 ? rlvData[0]?.solde : 0 || 0) +
      calculateTotalsop(
        checkedOps.filter((op) => !op.checked),
        "debit"
      );

    const totalCreditOps =
      Math.abs(rlvData[0]?.solde < 0 ? rlvData[0]?.solde : 0 || 0) +
      calculateTotalsop(
        checkedOps.filter((op) => !op.checked),
        "credit"
      );

    const totalDebitBq =
      (soldeBQ > 0 ? soldeBQ : 0 || 0) +
      calculateTotalsbq(
        checkedOpBq.filter((tr) => !tr.checked),
        "debit"
      );

    const totalCreditBq =
      Math.abs(soldeBQ < 0 ? soldeBQ : 0 || 0) +
      calculateTotalsbq(
        checkedOpBq.filter((tr) => !tr.checked),
        "credit"
      );

    const balanceDebit = totalDebitOps - totalCreditOps;

    const dateNow = new Date();
    let copyIcon = " © ";

    return (
      <Document>
        <Page size="A4" style={styles.page}>
          {/* Header */}
          <View style={styles.headerPage}>
            <View style={styles.leftSide}>
              <Text>{organisation?.name}</Text>
              <Text>{t("Impression provisoire")}</Text>
            </View>
            <View style={styles.centerContainer}>
              <Image style={{ width: 40, height: 30 }} src={smalLogo}></Image>
              <Text
                style={{
                  textAlign: "center",
                  fontSize: 10,
                  fontWeight: "bold",
                }}
              >
                {t("Etat de Rapprochement")}
              </Text>
              <View style={{ flexDirection: "row" }}>
                <Text style={{ marginRight: 30, fontSize: 8 }}>
                  {t("Compte bancaire")} :{" "}
                  {purchaseAccounts?.find(
                    (account) => account.id == selectedRlvBq?.compte_bancaire
                  )?.code || ""}
                </Text>
                <Text style={{ fontSize: 8 }}>
                  {t("Relevé N°")}: RLV-{selectedRlvBq?.id}
                </Text>
              </View>
            </View>
            <View style={styles.rightSide}>
              <View style={{ flexDirection: "row", justifyContent: "flex-end" }}>
                <Text style={{ textAlign: "right", marginRight: 4 }}>
                  {t("Date de rapprochement")}:
                </Text>
                <Text style={{ textAlign: "right" }}>{lastDay}</Text>
              </View>
            </View>
          </View>
          <View style={styles.headerPage}>
            <View style={styles.leftSide}>
              <Text style={{ fontSize: 9 }}>
                {copyIcon} {t("ELMANAGER COMMERCIALISATION ET COMPTABILITE")}
              </Text>
            </View>
            <View style={{ flexDirection: "row", alignItems: "flex-end" }}>
              <Text style={{ textAlign: "right", marginRight: 4 }}>
                {t("Date de tirage")}:
              </Text>
              <Text style={{ textAlign: "right" }}>
                {dateNow.getDate()}/{dateNow.getMonth() + 1}/
                {dateNow.getFullYear()} {t("à")} {dateNow.getHours()}:
                {dateNow.getMinutes().toString().padStart(2, "0")}
              </Text>
            </View>
          </View>
          {/* Table */}
          <View style={styles.table}>
            {/* Table Header */}
            <View style={styles.tableRow}>
              <View style={styles.tableColDate}>
                <Text style={styles.tableCell}>{t("DATE")}</Text>
              </View>
              <View style={styles.tableColPC}>
                <Text style={styles.tableCell}>{t("PC N°")}</Text>
              </View>
              <View style={styles.tableColLibelle}>
                <Text style={styles.tableCell}>{t("LIBELLE")}</Text>
              </View>
              <View style={styles.tableColDebitCreditHe}>
                <Text style={styles.tableCell}>
                  {t("NON ENREGISTRÉ EN COMPTABILITÉ")}
                </Text>
              </View>
              <View style={styles.tableColDebitCreditHe}>
                <Text style={styles.tableCell}>
                  {t("NON ENREGISTRÉ EN BANQUE")}
                </Text>
              </View>
            </View>
            <View style={styles.tableRow}>
              <View style={styles.tableColDate}></View>
              <View style={styles.tableColPC}></View>
              <View style={styles.tableColLibelle}></View>
              <View style={styles.tableColDebitCredit}>
                <Text style={styles.tableCell}>{t("DEBIT")}</Text>
              </View>
              <View style={styles.tableColDebitCredit}>
                <Text style={styles.tableCell}>{t("CREDIT")}</Text>
              </View>
              <View style={styles.tableColDebitCredit}>
                <Text style={styles.tableCell}>{t("DEBIT")}</Text>
              </View>
              <View style={styles.tableColDebitCredit}>
                <Text style={styles.tableCell}>{t("CREDIT")}</Text>
              </View>
            </View>
            {/* Initial Balances Row */}
            <View style={styles.tableRow}>
              <View style={styles.tableColDate}></View>
              <View style={styles.tableColPC}></View>
              <View style={styles.tableColLibelle}>
                <Text style={styles.tableCell}>{t("SOLDE DU : ")}{lastDay}</Text>
              </View>
              <View style={styles.tableColDebitCredit}>
                <Text style={styles.tableCell}>
                  {(rlvData[0]?.solde || 0) > 0
                    ? (rlvData[0]?.solde || 0).toFixed(2)
                    : ""}
                </Text>
              </View>
              <View style={styles.tableColDebitCredit}>
                <Text style={styles.tableCell}>
                  {(rlvData[0]?.solde || 0) < 0
                    ? Math.abs(rlvData[0]?.solde || 0).toFixed(2)
                    : ""}
                </Text>
              </View>
              <View style={styles.tableColDebitCredit}>
                <Text style={styles.tableCell}>
                  {(soldeBQ || 0) > 0 ? soldeBQ.toFixed(2) : ""}
                </Text>
              </View>
              <View style={styles.tableColDebitCredit}>
                <Text style={styles.tableCell}>
                  {(soldeBQ || 0) < 0 ? Math.abs(soldeBQ).toFixed(2) : ""}
                </Text>
              </View>
            </View>
            {/* Table Rows */}
            {checkedOps
              .filter((op) => !op.checked)
              .map((op, index) => (
                <View style={styles.tableRow} key={`op-${index}`}>
                  <View style={styles.tableColDate}>
                    <Text style={styles.tableCell}>{formatDate(op.date)}</Text>
                  </View>
                  <View style={styles.tableColPC}>
                    <Text style={styles.tableCell}>{op.id}</Text>
                  </View>
                  <View style={styles.tableColLibelle}>
                    <Text style={styles.tableCell}>
                      {op.transactions?.map((tr) => tr.libelle).join(", ")}
                    </Text>
                  </View>
                  <View style={styles.tableColDebitCredit}>
                    <Text style={styles.tableCell}>
                      {op.transactions?.[0]?.debit?.toFixed(2) || ""}
                    </Text>
                  </View>
                  <View style={styles.tableColDebitCredit}>
                    <Text style={styles.tableCell}>
                      {op.transactions?.[0]?.credit?.toFixed(2) || ""}
                    </Text>
                  </View>
                  <View style={styles.tableColDebitCredit}>
                    <Text style={styles.tableCell}></Text>
                  </View>
                  <View style={styles.tableColDebitCredit}>
                    <Text style={styles.tableCell}></Text>
                  </View>
                </View>
              ))}
            {checkedOpBq
              .filter((tr) => !tr.checked)
              .map((tr, index) => (
                <View style={styles.tableRow} key={`bq-${index}`}>
                  <View style={styles.tableColDate}>
                    <Text style={styles.tableCell}>{formatDate(tr.date)}</Text>
                  </View>
                  <View style={styles.tableColPC}>
                    <Text style={styles.tableCell}>{tr.id}</Text>
                  </View>
                  <View style={styles.tableColLibelle}>
                    <Text style={styles.tableCell}>{tr.libelle}</Text>
                  </View>
                  <View style={styles.tableColDebitCredit}>
                    <Text style={styles.tableCell}></Text>
                  </View>
                  <View style={styles.tableColDebitCredit}>
                    <Text style={styles.tableCell}></Text>
                  </View>
                  <View style={styles.tableColDebitCredit}>
                    <Text style={styles.tableCell}>
                      {tr.debit.toFixed(2) || ""}
                    </Text>
                  </View>
                  <View style={styles.tableColDebitCredit}>
                    <Text style={styles.tableCell}>
                      {tr.credit.toFixed(2) || ""}
                    </Text>
                  </View>
                </View>
              ))}
            {/* Totals */}
            <View style={styles.tableRow}>
              <View style={styles.tableColDate}>
                <Text style={styles.tableCell}></Text>
              </View>
              <View style={styles.tableColPC}>
                <Text style={styles.tableCell}></Text>
              </View>
              <View style={styles.tableColLibelle}>
                <Text style={styles.tableCell}>{t("TOTAL")}</Text>
              </View>
              <View style={styles.tableColDebitCredit}>
                <Text style={styles.tableCell}>{totalDebitOps.toFixed(2)}</Text>
              </View>
              <View style={styles.tableColDebitCredit}>
                <Text style={styles.tableCell}>
                  {totalCreditOps.toFixed(2)}
                </Text>
              </View>
              <View style={styles.tableColDebitCredit}>
                <Text style={styles.tableCell}>{totalDebitBq.toFixed(2)}</Text>
              </View>
              <View style={styles.tableColDebitCredit}>
                <Text style={styles.tableCell}>{totalCreditBq.toFixed(2)}</Text>
              </View>
            </View>
            {/* Balance */}
            <View style={styles.tableRow}>
              <View style={styles.tableColDate}>
                <Text style={styles.tableCell}></Text>
              </View>
              <View style={styles.tableColPC}>
                <Text style={styles.tableCell}></Text>
              </View>
              <View style={styles.tableColLibelle}>
                <Text style={styles.tableCell}>{t("BALANCE")}</Text>
              </View>
              <View style={styles.tableColDebitCredit}>
                <Text style={styles.tableCell}>
                  {balanceDebit > 0 ? balanceDebit.toFixed(2) : ""}
                </Text>
              </View>
              <View style={styles.tableColDebitCredit}>
                <Text style={styles.tableCell}>
                  {balanceDebit < 0 ? Math.abs(balanceDebit).toFixed(2) : ""}
                </Text>
              </View>
              <View style={styles.tableColDebitCredit}>
                <Text>
                  {parseFloat(totalDebitBq) - parseFloat(totalCreditBq) > 0
                    ? (
                        parseFloat(totalDebitBq) - parseFloat(totalCreditBq)
                      ).toFixed(2)
                    : " "}
                </Text>
              </View>
              <View style={styles.tableColDebitCredit}>
                <Text>
                  {parseFloat(totalDebitBq) - parseFloat(totalCreditBq) < 0
                    ? (
                        parseFloat(totalDebitBq) - parseFloat(totalCreditBq)
                      ).toFixed(2)
                    : " "}
                </Text>
              </View>
            </View>
          </View>
        </Page>
      </Document>
    );
  };

  return (
    <>
      <ToastContainer />
      <div className="container-fluid">
        <div className="row">
          <div className="col-lg-12">
            <div className="mb-4">
              <h1>{t("Gestion des relevés bancaires")}</h1>
              <p>
                {t(
                  "Bienvenue dans la section de gestion des relevés bancaires. Ici, vous pouvez consulter, rapprocher et gérer vos relevés bancaires de manière efficace."
                )}
              </p>
            </div>
            <div className="card">
              <div className="card-header container-fluid row">
                <div className="col-md-9 col-sm-12 card-title">
                  {t("Liste des relevés bancaires")}
                </div>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <div style={{ height: 600, width: "100%" }}>
                    <DataGrid
                      rows={rlvsBq}
                      columns={columns}
                      pageSize={5}
                      checkboxSelection
                      getRowId={(row) => row.id}
                      slots={{
                        loadingOverlay: LinearProgress,
                        toolbar: GridToolbar,
                      }}
                      loading={loading}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        className="modal fade"
        id="modalRlvBq"
        tabIndex="-1"
        aria-labelledby="modalRlvBqLabel"
        aria-hidden="true"
      >
        <ToastContainer />
        <div className="modal-dialog modal-fullscreen">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <span
                  className="badge rounded-pill bg-warning badge-sm me-1 mb-1 mt-1"
                  style={{ fontSize: 20 }}
                >
                  {readOnly
                    ? t("Détails du rapprochement bancaire")
                    : t("Rapprochement bancaire")}
                </span>
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              {modalLoading ? ( // Show loading indicator
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{ height: "100%" }}
                >
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">
                      {t("Chargement...")}
                    </span>
                  </div>
                </div>
              ) : (
                <>
                  {step === 1 ? (
                    <div className="container-fluid">
                      <div className="row" style={{ marginBottom: 10 }}>
                        <div className="col-md-3"></div>
                        <div className="col-md-6 col-sm-12">
                          <select
                            className="form-select"
                            value={selectedOption}
                            onChange={(e) => handleFilterChange(e.target.value)}
                          >
                            <option value="all">{t("Tous")}</option>
                            <option value="comptabliser">
                              {t("Comptablisé")}
                            </option>
                            <option value="non_comptabliser">
                              {t("Non comptablisé")}
                            </option>
                          </select>
                        </div>
                        <div className="col-md-3"></div>
                      </div>
                      <div className="row" style={{ height: "100%" }}>
                        <div
                          className="col-md-6"
                          style={{
                            borderRight: "1px solid #ccc",
                            height: "100%",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderBottom: "1px solid #ccc",
                              width: "100%",
                              height: "15vh",
                              padding: "10px",
                              marginBottom: "10px",
                            }}
                          >
                            <div>
                              <h4 style={{ margin: "5px 0" }}>
                                <span
                                  style={{ fontWeight: "bold", color: "#333" }}
                                >
                                  Relevé bancaire de
                                </span>
                                {` ${selectedRlvBq?.date || ""}`}
                              </h4>
                              <h4 style={{ margin: "5px 0" }}>
                                <span
                                  style={{ fontWeight: "bold", color: "#333" }}
                                >
                                  Compte bancaire
                                </span>
                                :{" "}
                                {purchaseAccounts?.find(
                                  (account) =>
                                    account.id === rlvData[0]?.account
                                )?.code || ""}{" "}
                                -{" "}
                                {purchaseAccounts?.find(
                                  (account) =>
                                    account.id === rlvData[0]?.account
                                )?.name || ""}
                              </h4>
                              <h4 style={{ margin: "5px 0" }}>
                                <span
                                  style={{ fontWeight: "bold", color: "#333" }}
                                >
                                  Solde
                                </span>
                                {` ${(rlvData[0]?.solde || 0).toFixed(2)} ${
                                  organisation?.currency?.code || ""
                                }`}
                              </h4>
                            </div>
                          </div>

                          <table className="table">
                            <thead className="text-center">
                              <tr>
                                <th></th>
                                <th>Date</th>
                                <th>Libellé</th>
                                <th>Account</th>
                                <th>Débit</th>
                                <th>Crédit</th>
                                <th>operation banque</th>
                              </tr>
                            </thead>
                            <tbody className="center">
                              {Array.isArray(checkedOps) ? (
                                checkedOps.map((op, index) => (
                                  <React.Fragment key={index}>
                                    <tr className="table">
                                      <td className="center">
                                        <label className="custom-control custom-checkbox-md">
                                          <input
                                            type="checkbox"
                                            className="custom-control-input"
                                            name={index}
                                            onChange={(e) =>
                                              handleCheckboxChangeOp(e, index)
                                            }
                                            checked={checkedOps.some(
                                              (item) =>
                                                item.indexOp === index &&
                                                item.checked
                                            )}
                                            disabled={readOnly}
                                          />
                                          <span className="custom-control-label"></span>
                                        </label>
                                      </td>
                                      <td className="center">
                                        {formatDate(op.date)}
                                      </td>
                                      <td colSpan={4}></td>
                                      <td className="text-center">
                                        {op.indexBq !== null
                                          ? op.indexBq + 1
                                          : ""}
                                      </td>
                                    </tr>
                                    {op.transactions?.map((tr, key) => (
                                      <tr key={key}>
                                        <td></td>
                                        <td></td>
                                        <td>{tr.libelle}</td>
                                        <td>
                                          <div
                                            style={{
                                              display: "flex",
                                              justifyContent: "center",
                                              width: "100%",
                                            }}
                                          >
                                            <select
                                              style={{ width: "130px" }}
                                              value={tr.account_id}
                                              disabled={true}
                                              className={"form-control"}
                                            >
                                              <option value={0}>
                                                _____________
                                              </option>
                                              {purchaseAccounts?.map(
                                                (account_p) => (
                                                  <option
                                                    key={account_p.id}
                                                    value={account_p.id}
                                                  >
                                                    {account_p.code} -{" "}
                                                    {account_p.name}
                                                  </option>
                                                )
                                              )}
                                            </select>
                                          </div>
                                        </td>
                                        <td>{tr.debit.toFixed(2)}</td>
                                        <td>{tr.credit.toFixed(2)}</td>
                                        <td>{}</td>
                                      </tr>
                                    ))}
                                  </React.Fragment>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={6} className="text-center">
                                    Aucune donnée disponible
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                        <div className="col-md-6">
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderBottom: "1px solid #ccc",
                              width: "100%",
                              height: "15vh",
                              padding: "10px",
                              marginBottom: "10px",
                            }}
                          >
                            <div>
                              <h4 style={{ margin: "5px 0" }}>
                                <span
                                  style={{ fontWeight: "bold", color: "#333" }}
                                >
                                  Liste des operations passer dans la periode
                                </span>
                                {` ${selectedRlvBq?.date || ""}`}
                              </h4>
                              <h4 style={{ margin: "5px 0" }}>
                                <span
                                  style={{ fontWeight: "bold", color: "#333" }}
                                >
                                  Compte bancaire
                                </span>
                                :{" "}
                                {purchaseAccounts?.find(
                                  (account) =>
                                    account.id === rlvData[0]?.account
                                )?.code || ""}{" "}
                                -{" "}
                                {purchaseAccounts?.find(
                                  (account) =>
                                    account.id === rlvData[0]?.account
                                )?.name || ""}
                              </h4>
                              <h4 style={{ margin: "5px 0" }}>
                                <span
                                  style={{ fontWeight: "bold", color: "#333" }}
                                >
                                  Solde
                                </span>

                                {` ${(soldeBQ || 0).toFixed(2)} ${
                                  organisation?.currency?.code || ""
                                }`}
                              </h4>
                            </div>
                          </div>
                          <table className="table">
                            <thead className="text-center">
                              <tr>
                                <th></th>
                                <th></th>
                                <th>Date</th>
                                <th>Libellé</th>
                                <th>Account</th>
                                <th>Débit</th>
                                <th>Crédit</th>
                              </tr>
                            </thead>
                            <tbody className="center">
                              {checkedOpBq?.map((tr, index) => (
                                <React.Fragment key={index}>
                                  <tr className="table">
                                    <td>{index + 1}</td>
                                    <td className="center">
                                      <label className="custom-control custom-checkbox-md">
                                        <input
                                          type="checkbox"
                                          className="custom-control-input"
                                          name={index}
                                          value={index}
                                          onChange={(e) => {
                                            handleCheckboxChangeBq(e, index);
                                          }}
                                          checked={
                                            (Array.isArray(checkedOpBq) &&
                                              checkedOpBq.some(
                                                (item) =>
                                                  item.indexBq === index &&
                                                  item.checked
                                              )) ||
                                            tr.comptaliser
                                          }
                                          disabled={readOnly}
                                        />
                                        <span class="custom-control-label"></span>
                                      </label>
                                    </td>
                                    <td className="center">
                                      {formatDate(tr.date)}
                                    </td>
                                    <td colSpan={4}></td>
                                  </tr>

                                  <tr key={index}>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td>{tr.libelle ? tr.libelle : " "}</td>
                                    <td>
                                      <div
                                        style={{
                                          display: "flex",
                                          justifyContent: "center",
                                          width: "100%",
                                        }}
                                      >
                                        <select
                                          style={{ width: "130px" }}
                                          value={tr.account}
                                          disabled={true}
                                          className={"form-control"}
                                        >
                                          <option value={0}>
                                            _____________
                                          </option>
                                          {purchaseAccounts.map((account_p) => (
                                            <option value={account_p.id}>
                                              {account_p.code} -{" "}
                                              {account_p.name}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                    </td>
                                    <td>{tr.debit.toFixed(2)}</td>
                                    <td>{tr.credit.toFixed(2)}</td>
                                  </tr>
                                </React.Fragment>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="container-fluid">
                      <div className="row">
                        <h1>{t("Etat de Rapprochement")}</h1>
                      </div>
                      <div className="row">
                        <table>
                          <tr>
                            <th colSpan="7" className="text-center">
                              {t("DATE DE RAPPROCHEMENT")}:{" "}
                              {(() => {
                                const [month, year] = (
                                  selectedRlvBq?.date || ""
                                ).split("/");
                                const lastDay = new Date(
                                  year,
                                  month,
                                  0
                                ).toLocaleDateString("fr-FR");
                                return lastDay;
                              })()}
                              <br />
                              {t("BANQUE")}:{" "}
                              {purchaseAccounts?.find(
                                (account) =>
                                  account.id == selectedRlvBq?.compte_bancaire
                              )
                                ? `${
                                    purchaseAccounts.find(
                                      (account) =>
                                        account.id ==
                                        selectedRlvBq?.compte_bancaire
                                    )?.code || ""
                                  } - ${
                                    purchaseAccounts.find(
                                      (account) =>
                                        account.id ==
                                        selectedRlvBq?.compte_bancaire
                                    )?.name || ""
                                  }`
                                : ""}
                            </th>
                          </tr>
                          <br />
                        </table>
                      </div>
                      <div className="row">
                        <div className="col-1"></div>
                        <div className="col-10">
                          <table className="table table-bordered">
                            <thead>
                              <tr>
                                <th>{t("DATE")}</th>
                                <th>{t("PC N°")}</th>
                                <th>{t("LIBELLE")}</th>
                                <th colSpan="2">
                                  {t("NON ENREGISTRÉ EN COMPTABILITÉ")}
                                </th>
                                <th colSpan="2">
                                  {t("NON ENREGISTRÉ EN BANQUE")}
                                </th>
                              </tr>
                              <tr>
                                <th></th>
                                <th></th>
                                <th></th>
                                <th>{t("DEBIT")}</th>
                                <th>{t("CREDIT")}</th>
                                <th>{t("DEBIT")}</th>
                                <th>{t("CREDIT")}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {/* Add the bank and statement balances */}
                              <tr>
                                <td></td>
                                <td></td>
                                <td>
                                  {t("SOLDE DU : ")}{" "}
                                  {(() => {
                                    const [month, year] = (
                                      selectedRlvBq?.date || ""
                                    ).split("/");
                                    const lastDay = new Date(
                                      year,
                                      month,
                                      0
                                    ).toLocaleDateString("fr-FR");
                                    return lastDay;
                                  })()}
                                </td>
                                <td>
                                  {(rlvData[0]?.solde || 0) > 0
                                    ? (rlvData[0]?.solde || 0).toFixed(2)
                                    : ""}
                                </td>
                                <td>
                                  {(rlvData[0]?.solde || 0) < 0
                                    ? Math.abs(rlvData[0]?.solde || 0).toFixed(
                                        2
                                      )
                                    : ""}
                                </td>
                                <td>
                                  {(soldeBQ || 0) > 0
                                    ? (soldeBQ || 0).toFixed(2)
                                    : ""}
                                </td>
                                <td>
                                  {(soldeBQ || 0) < 0
                                    ? Math.abs(soldeBQ || 0).toFixed(2)
                                    : ""}
                                </td>
                              </tr>
                              {checkedOps
                                .filter((op) => !op.checked) // Exclude checked operations
                                .map((op, index) => (
                                  <tr key={`op-${index}`}>
                                    <td>{formatDate(op.date)}</td>
                                    <td>{op.id}</td>
                                    <td>
                                      {op.transactions
                                        ?.map((tr) => tr.libelle)
                                        .join(", ")}
                                    </td>
                                    <td>
                                      {op.transactions?.[0]?.debit?.toFixed(
                                        2
                                      ) || 0}{" "}
                                      {/* Only calculate the first transaction */}
                                    </td>
                                    <td>
                                      {op.transactions?.[0]?.credit?.toFixed(
                                        2
                                      ) || 0}{" "}
                                      {/* Only calculate the first transaction */}
                                    </td>
                                    <td></td>
                                    <td></td>
                                  </tr>
                                ))}
                              {checkedOpBq
                                .filter((tr) => !tr.checked) // Exclude checked bank transactions
                                .map((tr, index) => (
                                  <tr key={`bq-${index}`}>
                                    <td>{formatDate(tr.date)}</td>
                                    <td>{tr.id}</td>
                                    <td>{tr.libelle}</td>
                                    <td></td>
                                    <td></td>
                                    <td>{tr.debit.toFixed(2)}</td>
                                    <td>{tr.credit.toFixed(2)}</td>
                                  </tr>
                                ))}
                            </tbody>
                            <tfoot>
                              <tr>
                                <td colSpan="3">{t("TOTAL")}</td>
                                <td>{totalDebitOps.toFixed(2)}</td>
                                <td>{totalCreditOps.toFixed(2)}</td>
                                <td>{totalDebitBq.toFixed(2)}</td>
                                <td>{totalCreditBq.toFixed(2)}</td>
                              </tr>
                              <tr>
                                <td colSpan="3">{t("BALANCE")}</td>
                                <td>
                                  {(() => {
                                    const balance =
                                      totalDebitOps - totalCreditOps;
                                    return balance > 0
                                      ? balance.toFixed(2)
                                      : "";
                                  })()}
                                </td>
                                <td>
                                  {(() => {
                                    const balance =
                                      totalDebitOps - totalCreditOps;
                                    return balance < 0
                                      ? Math.abs(balance).toFixed(2)
                                      : "";
                                  })()}
                                </td>
                                <td>
                                  {(() => {
                                    const balance =
                                      totalDebitBq - totalCreditBq;
                                    return balance > 0
                                      ? balance.toFixed(2)
                                      : "";
                                  })()}
                                </td>
                                <td>
                                  {(() => {
                                    const balance =
                                      totalDebitBq - totalCreditBq;
                                    return balance < 0
                                      ? Math.abs(balance).toFixed(2)
                                      : "";
                                  })()}
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                        <div className="col-1"></div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-danger"
                data-bs-dismiss="modal"
                disabled={actionLoading} // Disable button during loading
              >
                {t("Fermer")}
              </button>
              {step > 1 && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setStep(step - 1)} // Go to the previous step
                  disabled={actionLoading} // Disable button during loading
                >
                  {t("Précédent")}
                </button>
              )}
              {step < 2 && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setStep(step + 1)} // Go to the next step
                  disabled={actionLoading} // Disable button during loading
                >
                  {t("Suivant")}
                </button>
              )}
              {!readOnly && step !== 2 && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handelSave}
                  disabled={actionLoading} // Disable button during loading
                >
                  {actionLoading ? (
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                  ) : (
                    t("Enregistrer")
                  )}
                </button>
              )}
              {selectedRlvBq && step === 2 && (
                <PDFDownloadLink
                  document={<ExportRlv />}
                  fileName={`Etat_Rapprochement_${
                    selectedRlvBq?.date || ""
                  }.pdf`}
                  className="btn btn-success"
                >
                  {({ blob, url, loading, error }) => {
                    if (loading) return t("Chargement...");
                    if (error) {
                      console.log(error);
                      return t("Erreur lors de la génération du PDF");
                    }
                    return t("Télécharger PDF");
                  }}
                </PDFDownloadLink>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RlvBqListe;
